import { brandApi } from '../lib/axios';
import { UserPermissions, PlanFeature } from '../types/permissions';

export interface PlanFeatureInfo {
  name: string;
}

export interface PlanLimitsInfo {
  approvedReferralsPerMonth: number | 'unlimited';
  maxAffiliates: number | 'unlimited';
  maxPrograms: number | 'unlimited';
}

export interface PlanDescriptionInfo {
  id: string;
  description: string;
  order: number;
}

export interface AvailablePlan {
  id: string;
  name: string;
  price: number;
  commissionRate: number;
  /** Monthly AI search questions included with the plan. */
  aiSearchQuestions?: number;
  features: PlanFeatureInfo[];
  limits: PlanLimitsInfo;
  descriptions?: PlanDescriptionInfo[];
}

class PermissionsService {
  /**
   * Fetch current user's permissions from the backend
   */
  async getMyPermissions(): Promise<UserPermissions> {
    const response = await brandApi.get('/permissions/me');
    return response.data;
  }

  /**
   * Fetch all available plans from the backend
   */
  async getAvailablePlans(): Promise<AvailablePlan[]> {
    const response = await brandApi.get('/permissions/plans');
    return response.data;
  }

  /**
   * Check if the current user has a specific feature
   */
  hasFeature(permissions: UserPermissions | null, feature: PlanFeature): boolean {
    if (!permissions) return false;
    return permissions.features.includes(feature);
  }

  /**
   * Check if the current user has any of the specified features
   */
  hasAnyFeature(permissions: UserPermissions | null, features: PlanFeature[]): boolean {
    if (!permissions) return false;
    return features.some(feature => permissions.features.includes(feature));
  }

  /**
   * Check if the current user has all of the specified features
   */
  hasAllFeatures(permissions: UserPermissions | null, features: PlanFeature[]): boolean {
    if (!permissions) return false;
    return features.every(feature => permissions.features.includes(feature));
  }
}

export const permissionsService = new PermissionsService();
